import { Link } from "react-router-dom";
import { Star, Plus, Flame, Store } from "lucide-react";
import { resolveImage } from "../lib/image";
import { fmt } from "../lib/money";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { canSeeAffiliateUI } from "../lib/permissions";
import api from "../lib/api";

export default function ProductCard({ product, index = 0, quickAddSource = null, recStrategy = null }) {
  const img = resolveImage(product.images?.[0]) || "https://images.unsplash.com/photo-1611078489935-0cb964de46d6?w=800";
  const { add } = useCart();
  const { user } = useAuth();
  const showAffiliate = canSeeAffiliateUI(user);
  const isPos = product.source === "pos";
  const showQuickAdd = !!quickAddSource && !isPos && !product.sizes?.length && !product.colors?.length;

  // Fire-and-forget: tell the backend this card was clicked from a rec surface.
  // Powers both per-product click analytics (admin) and the HOT rotation.
  const trackRecClick = () => {
    if (!quickAddSource) return;
    api.post("/rec/click", {
      product_id: product.product_id,
      source: quickAddSource,
      strategy: recStrategy || undefined,
    }).catch(() => {});
  };

  return (
    <div className="relative group fade-up" style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}>
      <Link
        to={`/products/${product.product_id}`}
        data-testid={`product-card-${product.product_id}`}
        onClick={trackRecClick}
        className="block"
      >
        <div className="aspect-square overflow-hidden bg-neutral-100 border border-neutral-200 relative">
          <img
            src={img}
            alt={product.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          {showAffiliate && product.affiliate_commission_pct > 0 && (() => {
            const earn = Math.round((product.price || 0) * product.affiliate_commission_pct / 100);
            return (
              <span
                data-testid={`earn-badge-${product.product_id}`}
                className="absolute top-2 left-2 bg-[#FFE600] text-black text-[11px] font-display font-black uppercase tracking-wide px-2 py-1 shadow-[2px_2px_0_0_#000]"
                title={`Share this product to earn ${product.affiliate_commission_pct}% (${fmt(earn)}) commission per sale`}
              >
                Earn {earn > 0 ? fmt(earn) : `${product.affiliate_commission_pct}%`}
              </span>
            );
          })()}
          {product.trending && (
            <span
              data-testid={`trending-badge-${product.product_id}`}
              className={`absolute ${showAffiliate && product.affiliate_commission_pct > 0 ? "top-10" : "top-2"} left-2 bg-[#FF2A00] text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1`}
            >
              <Flame className="w-3 h-3" /> Trending
            </span>
          )}
          {isPos && (
            <span
              data-testid={`pos-badge-${product.product_id}`}
              className="absolute top-2 right-2 bg-black text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1"
              title={`Sold via ${product.merchant?.name || product.seller_name} (POS)`}
            >
              <Store className="w-3 h-3" /> {product.merchant?.name || product.seller_name}
            </span>
          )}
        </div>
        <div className="pt-3 pb-5">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1">{product.category}</div>
          <h3 className="font-display font-bold text-base leading-tight line-clamp-2 group-hover:text-[#0033FF] transition-colors">
            {product.title}
          </h3>
          <div className="flex items-center justify-between mt-2">
            <span className="font-display font-black text-lg">{fmt(product.price)}</span>
            <div className="flex items-center gap-1 text-xs text-neutral-600">
              <Star className="w-3.5 h-3.5 fill-black stroke-black" />
              <span>{product.rating?.toFixed(1) || "—"}</span>
              <span className="text-neutral-400">({product.reviews_count || 0})</span>
            </div>
          </div>
        </div>
      </Link>
      {showQuickAdd && (
        <button
          data-testid={`rec-add-${product.product_id}`}
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            add({ product_id: product.product_id, quantity: 1, source: quickAddSource });
          }}
          aria-label={`Add ${product.title} to cart`}
          className="absolute top-2 right-2 w-9 h-9 bg-black text-white hover:bg-[#0033FF] opacity-0 group-hover:opacity-100 transition-all grid place-items-center"
        >
          <Plus className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
